/**
 * Zod schemas for the Service Keys admin API.
 *
 * GET  /api/admin/service-keys → ServiceKeysListResponseSchema
 * POST /api/admin/service-keys → ServiceKeyCreateResponseSchema
 * DELETE /api/admin/service-keys/:keyId → (empty 200)
 *
 * SECURITY: tokenHash is intentionally absent from all list/item schemas.
 * rawToken appears only in ServiceKeyCreateResponseSchema (returned once
 * on creation) and MUST NOT appear in any other schema.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Scope registry shapes (returned inline with the list response)
// ---------------------------------------------------------------------------

export const ScopeDomainSchema = z.looseObject({
  domain: z.string().min(1),
  label: z.string().min(1),
  description: z.string(),
  verbs: z.array(z.enum(['read', 'write'])),
});

export type ScopeDomain = z.infer<typeof ScopeDomainSchema>;

export const ScopeNamespaceRegistrySchema = z.looseObject({
  moduleKey: z.string().min(1),
  domains: z.array(ScopeDomainSchema),
});

export type ScopeNamespaceRegistry = z.infer<typeof ScopeNamespaceRegistrySchema>;

// ---------------------------------------------------------------------------
// Service key list item — NO tokenHash, NO rawToken
// ---------------------------------------------------------------------------

export const ServiceKeyListItemSchema = z.looseObject({
  keyId: z.string().min(1),
  label: z.string().min(1),
  description: z.string(),
  scopes: z.array(z.string()),
  opcoId: z.string().nullable().optional(),
  status: z.enum(['active', 'revoked']),
  keyVersion: z.string().optional(),
  createdAt: z.string(),
  createdBy: z.string(),
  lastUsedAt: z.string().nullable().optional(),
  revokedAt: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
});

export type ServiceKeyListItem = z.infer<typeof ServiceKeyListItemSchema>;

// ---------------------------------------------------------------------------
// GET /api/admin/service-keys response
// ---------------------------------------------------------------------------

export const ServiceKeysListResponseSchema = z.looseObject({
  keys: z.array(ServiceKeyListItemSchema),
  registry: z.array(ScopeNamespaceRegistrySchema),
});

export type ServiceKeysListResponse = z.infer<typeof ServiceKeysListResponseSchema>;

// ---------------------------------------------------------------------------
// POST /api/admin/service-keys response — rawToken shown ONCE
// ---------------------------------------------------------------------------

export const ServiceKeyCreateResponseSchema = z.looseObject({
  keyId: z.string().min(1),
  rawToken: z.string().min(40),
  label: z.string().min(1),
  description: z.string().optional(),
  scopes: z.array(z.string()),
  opcoId: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
  createdAt: z.string(),
});

export type ServiceKeyCreateResponse = z.infer<typeof ServiceKeyCreateResponseSchema>;
